import { useRef, useState } from "react";
import axios from "axios";
import { Camera, Mic, Square, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function fileToBase64Jpeg(file, max = 1024) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.86);
}

export function PhotoButton({ photo, onPhoto, onClear, label, disabled }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { onPhoto(await fileToBase64Jpeg(f)); } catch (_) {} finally { setBusy(false); }
  };
  if (photo) {
    return (
      <div className="relative inline-flex items-center gap-2 rounded-full border-2 border-fuchsia-300 bg-white pl-1 pr-3 py-1 shadow-sm" data-testid="lumi-photo-preview">
        <img src={photo} alt="" className="w-9 h-9 rounded-full object-cover" />
        <span className="text-xs font-bold text-fuchsia-700">{label.photoReady}</span>
        <button type="button" onClick={onClear} aria-label="remove photo" data-testid="lumi-photo-clear" className="ml-1 w-6 h-6 rounded-full bg-slate-100 hover:bg-rose-100 text-slate-600 flex items-center justify-center">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={pick} data-testid="lumi-photo-input" />
      <button type="button" disabled={disabled || busy} onClick={() => inputRef.current?.click()} data-testid="lumi-photo-button"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-fuchsia-300 hover:border-fuchsia-500 hover:bg-fuchsia-50 text-fuchsia-800 text-xs sm:text-sm font-bold shadow-sm transition disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        {label.photo}
      </button>
    </>
  );
}

export function MicButton({ onTranscript, label, disabled }) {
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const [state, setState] = useState("idle"); // idle | recording | transcribing | error
  const [err, setErr] = useState("");

  const start = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setState("transcribing");
        try {
          const fd = new FormData();
          fd.append("audio", blob, (rec.mimeType || "").includes("mp4") ? "clip.mp4" : "clip.webm");
          const { data } = await axios.post(`${API}/store/skin-advisor/transcribe`, fd, { timeout: 60000 });
          onTranscript(data.text || "", data.language || null);
          setState("idle");
        } catch (e) {
          setErr(e?.response?.data?.detail || label.micError);
          setState("error");
          setTimeout(() => setState("idle"), 3000);
        }
      };
      rec.start();
      recRef.current = rec;
      setState("recording");
    } catch (_) {
      setErr(label.micDenied);
      setState("error");
      setTimeout(() => setState("idle"), 3000);
    }
  };
  const stop = () => { try { recRef.current?.stop(); } catch (_) {} };

  const recording = state === "recording";
  return (
    <div className="inline-flex flex-col items-start gap-1">
      <button type="button" disabled={disabled || state === "transcribing"} onClick={recording ? stop : start} data-testid="lumi-mic-button"
        aria-label={recording ? label.micStop : label.mic}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-bold shadow-sm transition disabled:opacity-50 border-2 ${
          recording ? "bg-rose-600 border-rose-600 text-white animate-pulse" : "bg-white border-fuchsia-300 hover:border-fuchsia-500 hover:bg-fuchsia-50 text-fuchsia-800"}`}>
        {state === "transcribing" ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        {state === "transcribing" ? label.micBusy : recording ? label.micStop : label.mic}
      </button>
      {err && <span className="text-[11px] text-rose-700" data-testid="lumi-mic-error">{err}</span>}
    </div>
  );
}
